import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Car, Home, Heart, Briefcase, Users, Bus, Phone, Calendar, Bike, Plane, PawPrint } from 'lucide-react';
import { openCalendlyPopup } from '@/utils/calendly';
import WhatsAppButton from '@/components/WhatsAppButton';
const Particulieren = () => {
  const insuranceTypes = [{
    icon: Car,
    title: 'Autoverzekering',
    description: 'Van WA tot volledig casco. Wij zoeken de beste dekking voor jouw auto tegen de scherpste prijs.',
    features: ['WA verzekering', 'WA+ verzekering', 'All-risk verzekering', 'Schade-expert service']
  }, {
    icon: Home,
    title: 'Woonverzekering',
    description: 'Bescherm je huis en inboedel tegen brand, inbraak, storm en andere schades.',
    features: ['Opstalverzekering', 'Inboedelverzekering', 'Aansprakelijkheidsverzekering', '24/7 calamiteitenservice']
  }, {
    icon: Bike,
    title: 'Motorverzekering',
    description: 'Verzeker je motor optimaal tegen de beste voorwaarden en scherpste premie.',
    features: ['WA verzekering', 'WA+ verzekering', 'Volledig casco', 'Motorhulp service']
  }, {
    icon: Plane,
    title: 'Reisverzekering',
    description: 'Zorgeloos op vakantie met de juiste reisverzekering voor elke bestemming.',
    features: ['Medische kosten', 'Annuleringsdekking', 'Bagage verzekering', 'Wereldwijde dekking']
  }, {
    icon: Heart,
    title: 'Levensverzekering',
    description: 'Zekerheid voor je familie bij overlijden of voor het opbouwen van kapitaal.',
    features: ['Overlijdensrisicoverzekering', 'Uitvaartverzekering', 'Kapitaalverzekering', 'Hypotheekbescherming']
  }, {
    icon: Users,
    title: 'Particuliere aansprakelijkheid',
    description: 'Bescherming tegen schade die je onbedoeld aan anderen toebrengt.',
    features: ['Persoonlijke schade', 'Materiele schade', 'Rechtsbijstand', 'Wereldwijde dekking']
  }, {
    icon: Briefcase,
    title: 'Rechtsbijstandverzekering',
    description: 'Juridische bijstand bij alle geschillen in je dagelijks leven.',
    features: ['Contractgeschillen', 'Arbeidsrecht', 'Verkeersrecht', '24/7 juridisch advies']
  }, {
    icon: Bus,
    title: 'Caravanverzekering',
    description: 'Bescherm je caravan of camper tegen diefstal, schade en andere risico\'s.',
    features: ['Diefstal', 'Hagelschade', 'Inventaris', 'Caravanhulp']
  }, {
    icon: PawPrint,
    title: 'Huisdierenverzekering',
    description: 'Zorg voor je huisdier zonder zorgen over de kosten bij ziekte of ongelukken.',
    features: ['Dierenarts kosten', 'Operaties', 'Medicijnen', 'Specialistische zorg']
  }];
  return <div className="min-h-screen">
      <Helmet>
        <title>Particuliere Verzekeringen - Compleet Verzekerd</title>
        <meta name="description" content="Alle particuliere verzekeringen onder één dak. Auto, woning, motorfiets, reis, leven, aansprakelijkheid, caravan en huisdier. Persoonlijk advies!" />
        <meta name="keywords" content="particuliere verzekeringen, autoverzekering, woonhuisverzekering, motorverzekering, reisverzekering, levensverzekering, aansprakelijkheidsverzekering, caravanverzekering, huisdierverzekering" />
        <link rel="canonical" href="https://compleetverzekerd.nl/particulieren/" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-hero text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Particuliere verzekeringen</h1>
            <p className="text-xl mb-8">
              Bescherm jezelf en je gezin met de juiste verzekeringen. 
              Wij helpen je bij het maken van de beste keuze voor jouw situatie.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-3" onClick={openCalendlyPopup} aria-label="Plan een afspraak">
                <Calendar className="w-5 h-5" />
                Plan een afspraak
              </Button>
              <Button variant="outline-inverse" size="lg" asChild>
                  <a href="tel:0782200021">
                    <Phone className="h-5 w-5" />
                    078 - 220 00 21
                  </a>
                </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Types */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-secondary mb-4">Onze particuliere verzekeringen</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Van autoverzekering tot levensverzekering - wij regelen het voor je
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {insuranceTypes.map((insurance, index) => {
            const Icon = insurance.icon;
            return <Card key={index} className="p-6 hover:shadow-card transition-all duration-300 h-full">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-secondary mb-3">
                      {insurance.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {insurance.description}
                    </p>
                  </div>

                  <div className="space-y-2 mb-6">
                    {insurance.features.map((feature, featureIndex) => <div key={featureIndex} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span className="text-sm">{feature}</span>
                      </div>)}
                  </div>

                  
                </Card>;
          })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-secondary mb-6">
            Klaar om je verzekeringen te optimaliseren?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto text-base">
            Je kunt een afspraak inplannen via de website of even bellen met een van onze medewerkers. Je krijgt geen keuze menu maar gewoon contact met een adviseur of assistent. De afspraak kan plaatsvinden bij ons op kantoor of online via een videogesprek. Een gesprek duurt meestal 15 tot 60 minuten, afhankelijk van jouw situatie. Er worden geen kosten in rekening gebracht voor een adviesgesprek. In veel gevallen is een afspraak niet eens nodig en kunnen we je direct telefonisch al verder helpen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-primary hover:bg-primary-dark" onClick={openCalendlyPopup}>
              <Calendar className="h-5 w-5" />
              Plan een afspraak
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="tel:0782200021">
                <Phone className="h-5 w-5" />
                078 - 220 00 21
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>;
};
export default Particulieren;